import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { PcrProcessLog, PcrProcessLogForm, PcrProcessLogSearchForm } from 'entities/pcr_process_log'

type PcrProcessLogsResponse = PagingResponse & {
    results: PcrProcessLog[]
}

export function useFetchPcrProcessLogsApi(searchForm?: Form<PcrProcessLogSearchForm>): IndexApiSet<PcrProcessLogsResponse> & { execute: () => void } {
    const apiPath = 'pcr_process_logs/'
    const api = useIndexApi<PcrProcessLogsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchPcrProcessLogApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `pcr_process_logs/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostPcrProcessLogApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrProcessLogForm>) => void } {
    const apiSet = usePostApi<BaseResponse, PcrProcessLogForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<PcrProcessLogForm>) => {
        const apiPath = `pcr_process_logs/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutPcrProcessLogApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrProcessLogForm>) => void } {
    const apiSet = usePutApi<BaseResponse, PcrProcessLogForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<PcrProcessLogForm>) => {
        const apiPath = `pcr_process_logs/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutPcrProcessLogsApi(): ApiSet<BaseResponse> & { execute: (form: Form<PcrProcessLogForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, PcrProcessLogForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<PcrProcessLogForm[]>) => {
        const apiPath = `pcr_process_logs/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeletePcrProcessLogApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `pcr_process_logs/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
